import { REPLACE_POLICY } from './replacePolicy';
import { get, set, cloneDeep, merge, omit, pick } from "lodash-es";
import { replace as plainReplace } from './replaceFunction/plainReplace';
import { replace as tagReplace } from './replaceFunction/tagReplace';
import { replace as containerReplace } from './replaceFunction/containerReplace';
import { UICorePlugin } from "../../../../lib/Plugin";
import { genPlaceCompInsideAlternates, applyPlaceCompInside } from "../../../../lib/builtin/plugins/CompActions/placeCompInsideActions";
import { vueBailDepCollector } from "../../../../lib/utils/vue";
export function getCompMeta(ctx, id) {
  return ctx.uc.components[id];
}
const InternalReplaceCallback = {
  plainReplace,
  tagReplace,
  containerReplace
};
const compIsContainer = meta => (meta === null || meta === void 0 ? void 0 : meta.category) === '布局';
const checkCtxForReplace = ctx => {
  if (!ctx.meta) return false;
  return true;
};
function checkReplacePolicy(item) {
  return item.targetCompId && Array.isArray(item.sourceComponent) && item.sourceComponent.every(config => {
    if (config.replaceCallback && typeof config.replaceCallback !== 'function') return false;
    return !!config.id;
  });
}
export class CompReplacePlugin extends UICorePlugin {
  constructor() {
    super(...arguments);
    /** Set<组件type> 可以生成合法layout的组件type合集 */
    this.compWhiteList = new Set();
    /** Record<分类,order> 决定了不同组件分组出现的顺序 */
    this.compMaterialOrder = {};
    /** 组件生成的默认layout */
    this.compMaterialCache = {};
    /** 缓存转换到组件X的一组ReplaceConfig 代表着对X组件执行更换组件时 提供的通用替换策略 */
    this.replaceConfigsCache = new Map();
    this.replacePolicyCache = [...REPLACE_POLICY];
    this.loadedReplacePolicyFromComp = false;
  }
  /** 用来初始化compMaterialOrder和compWhiteList */
  buildCompWhiteList(ctx) {
    // 懒加载只收集一次
    if (this.compWhiteList.size > 0) return;
    const layoutAns = ctx.designer.getMaterialBoxGroups();
    layoutAns.forEach(group => {
      group.items.forEach(item => {
        const compType = get(item, ['layout', 'type']);
        // 可能有重复生成? 取最先生成的
        this.compMaterialCache[compType] = this.compMaterialCache[compType] || item.layout;
        this.compWhiteList.add(compType);
      });
      // 顺便把组件分类的优先级收集一下
      this.compMaterialOrder[group.name] = group.order;
    });
  }
  /** 懒加载初始化replaceConfigsCache */
  lazyReplaceConfigs(ctx, compId) {
    const configArrayCached = this.replaceConfigsCache.get(compId);
    if (configArrayCached) return configArrayCached;
    const replaceMetas = ctx.uc.componentsArray.filter(compMeta => this.compWhiteList.has(compMeta.id))
    // 必须是可见的 不然可能会把内置组件啥的给替换进去
    .filter(compMeta => compMeta.visible && compMeta.name && compMeta.type !== 'trigger');
    const replaceConfigs = replaceMetas.map(meta => ({
      // 从id替换为targetCompId 的一个策略
      id: compId,
      targetCompId: meta.id,
      getModeUsed: {
        plainReplace: true
      }
    }));
    this.replaceConfigsCache.set(compId, replaceConfigs);
    return replaceConfigs;
  }
  /** 根据一个ReplaceConfig 将 layout 替换为新的layout */
  config2Layout(config, ctx, layout) {
    if (!config.targetCompId) return;
    try {
      var _targetLayout;
      const mode = {
        customReplace: !!config.replaceCallback
      };
      if (typeof config.getModeUsed === 'function') {
        const configMode = config.getModeUsed({
          ctx,
          sourceLayout: layout
        });
        merge(mode, configMode);
      } else if (config.getModeUsed) {
        merge(mode, config.getModeUsed);
      }
      const sourceLayout = cloneDeep(layout);
      // 提取默认的targetlayout
      const originLayout = cloneDeep(this.compMaterialCache[config.targetCompId]);
      if (!originLayout) return;
      // 递归部分要全都拿过去
      originLayout.children = sourceLayout.children || originLayout.children;
      originLayout.slots = sourceLayout.slots || originLayout.slots;
      originLayout.scopedSlots = sourceLayout.scopedSlots || originLayout.scopedSlots;
      // 默认全都继承下来 然后想办法剪掉不合法的
      let targetLayout = merge({}, sourceLayout, originLayout);
      // 执行预设的替换模式
      Object.keys(mode).forEach(key => {
        const callback = InternalReplaceCallback[key];
        if (callback && mode[key]) {
          const {
            layout
          } = callback({
            ctx,
            sourceLayout,
            targetCompId: config.targetCompId,
            targetLayout
          });
          // 迭代输出 上一步的结果滚到下一步的输入里
          targetLayout = layout;
        }
      });
      // 统一输出的type 抹掉id
      if ((_targetLayout = targetLayout) !== null && _targetLayout !== void 0 && _targetLayout.type) {
        targetLayout.type = config.targetCompId;
        delete targetLayout.componentId;
      }
      // 执行用户自定义替换
      if (mode.customReplace && config.replaceCallback) {
        const {
          layout
        } = config.replaceCallback({
          ctx,
          sourceLayout,
          targetCompId: config.targetCompId,
          targetLayout
        });
        if (layout) {
          targetLayout = layout;
        }
      }
      return targetLayout;
    } catch (e) {
      console.error(e);
      return undefined;
    }
  }
  /** 普通替换回调 */
  genApplyCallback(config, ctx, sourceLayout, path) {
    const applyCallback = () => {
      const newLayout = this.config2Layout(config, ctx, sourceLayout);
      const layout = cloneDeep(newLayout);
      ctx.uc.completeLayout(layout);
      ctx.designer.replaceCompWith(path, layout);
    };
    return applyCallback;
  }
  /** 涉及到容器拆装实现的容器替换走这里 */
  genApplyCallbackForContainer(config, ctx, sourceLayout, path, containerSlot, payload) {
    const applyCallback = async () => {
      // 先保存内容物
      // 原本是 sourceLayout { containerSlot }
      // 现在要把sourceLayout 先换成 targetLayout 然后 把containerSLot装进去
      const newCfg = cloneDeep(config);
      set(newCfg, 'getModeUsed.containerReplace', false);
      const newLayout = this.config2Layout(newCfg, ctx, sourceLayout);
      const layout = cloneDeep(newLayout);
      ctx.uc.completeLayout(layout);
      // 这个layout就是替换好属性的layout
      // 先替换
      // 然后执行一次装入容器
      const slotKeys = ['children', 'slots', 'scopedSlots'];
      if (payload.getLayout) {
        const newPlaceLayout = await payload.getLayout(containerSlot);
        // newPlaceLayout和 layout 要做一次merge 其中插槽内容是newPlaceLayout优先  其余的是layout优先
        const finalLayout = merge({}, pick(newPlaceLayout, slotKeys), omit(layout, slotKeys));
        await ctx.designer.replaceCompWith(path, finalLayout);
        return;
      }
      if (payload.slotId) {
        const slotArray = cloneDeep(containerSlot);
        // 直接替换 然后把子内容重新装入到上面
        const finalLayout = merge({}, pick(payload.containerLayout, slotKeys), omit(layout, slotKeys));
        // 直接走一遍装入 使用替换过的containerLayout 直接替换到现在的位置上 然后装入的内容改为装入子内容数组
        await applyPlaceCompInside(ctx, {
          ...payload,
          containerLayout: finalLayout
        }, slotArray);
      }
    };
    return applyCallback;
  }
  loadReplacePolicyFromComp(uc) {
    if (this.loadedReplacePolicyFromComp) return;
    vueBailDepCollector(() => {
      uc.componentsArray.forEach(meta => {
        if (Array.isArray(meta.replaceConfig)) {
          meta.replaceConfig.forEach(item => {
            if (checkReplacePolicy(item)) {
              this.replacePolicyCache.push(item);
            }
          });
        }
      });
    });
    this.loadedReplacePolicyFromComp = true;
  }
  install(uc) {
    if (!uc.isDesignMode) return;
    uc.hooks.getReplaceComponent.tap('compReplace', (result, ctx) => {
      if (!checkCtxForReplace(ctx)) return result;
      const {
        meta,
        layout,
        path
      } = ctx;
      const getCompMeta = id => ctx.uc.components[id];
      const compId = meta.id;
      const replaceConfigs = [];
      // 按照被替换的id 筛选sourceComponent
      // 补全目标替换组件idtarget
      this.loadReplacePolicyFromComp(uc);
      this.replacePolicyCache.forEach(replaceConfig => {
        const sourceConfig = replaceConfig.sourceComponent.filter(({
          id
        }) => id === compId).map(config => ({
          ...config,
          targetCompId: replaceConfig.targetCompId
        }));
        replaceConfigs.push(...sourceConfig);
      });
      const applyCallback = item => {
        const layout = cloneDeep(item.layout);
        ctx.uc.completeLayout(layout);
        ctx.designer.replaceCompWith(path, layout);
      };
      const replaceItem = replaceConfigs.map(config => {
        const targetLayout = this.config2Layout(config, ctx, layout);
        if (!targetLayout) return undefined;
        return {
          ...getCompMeta(config.targetCompId),
          layout: targetLayout,
          order: config.order,
          typeCName: config.groupName || '替换组件插件',
          applyCallback,
          // ui-core中的最高优先级
          type: 'editor'
        };
      }).filter(Boolean);
      return [...replaceItem, ...result];
      // 按照meta 从配置文件中找到配置项 然后筛选出所有能替换的部分 连同回调一块塞回结果里
    });
    // 用来提供全部其他无关组件替换的功能 组件比较多要注意一下性能问题 大多数操作要到用户点击的时候再进行apply
    uc.hooks.getReplaceComponent.tap('compReplaceAll', (result, ctx) => vueBailDepCollector(() => {
      if (!checkCtxForReplace(ctx)) return result;
      // TODO: 后续支持触发器的替换
      if (ctx.path.startsWith('/@triggers')) return result;
      // 懒加载组件白名单 需要保证至少能生成出一份layout material
      this.buildCompWhiteList(ctx);
      const {
        meta,
        layout,
        path
      } = ctx;
      const getCompMeta = id => ctx.uc.components[id];
      const compId = meta.id;
      // 已经在推荐列表里的id
      const resultAppearedIdSet = new Set(result.map(item => get(item, 'layout.type') || get(item, 'id')));
      // 3个插槽 随便挑点东西出来 后续用拆除容器搞定
      const containerSlot = [...get(layout, 'children', []), ...Object.values(layout.slots || {}).flat(), ...Object.values(layout.scopedSlots || {}).map(item => item.layout)];
      const seemsToContainer = (compIsContainer(meta) || meta.placeCompInside) && containerSlot.length > 0;
      // 替换为meta时要有一些基础内容写入Option
      const getBaseReplaceComponentOption = meta => ({
        name: meta.name,
        icon: meta.icon,
        order: 0,
        // 仅用于提供索引 真正的layout在applyCallback中生成
        layout: {
          type: meta.id
        },
        typeCName: `${meta.category || '其他'}`,
        // 应该扔个比display还低级的 按照 fitDataSort 的逻辑扔个空的就可以被concat到尾部了
        type: `${this.compMaterialOrder[meta.category || '']}`
      });
      const ops = [];
      // 如果是容器并且有内容 复用装入容器功能 或者有内容+容器鸭子也行
      if (seemsToContainer) {
        const containerList = genPlaceCompInsideAlternates(ctx);
        // 这次是尝试将内容物进行装入容器 所以从装入容器的列表里进一步生成
        const replaceOptions = containerList.map(payload => {
          const {
            meta
          } = payload;
          // config已经没意义了其实 替换逻辑和designer重度耦合怎么办 需要后面拆解designer.spliceComp?
          const config = this.lazyReplaceConfigs(ctx, compId).find(cfg => cfg.targetCompId === meta.id);
          return {
            ...getBaseReplaceComponentOption(meta),
            applyCallback: this.genApplyCallbackForContainer(config, ctx, layout, path, containerSlot, payload)
          };
        });
        ops.push(...replaceOptions);
      }
      // 容器的搞定了 剩下的一律当做无内容进行替换
      {
        // 没在推荐列表里的 就要都扔到那个无关但是可以替换的分组里
        const replaceConfigs = this.lazyReplaceConfigs(ctx, compId)
        // 非容器对非容器 无内容的容器对容器 (无内容的容器可以替换成输入框吗? 待定 无内容的鸭子容器可以替换成容器吗 待定)
        .filter(config => {
          const cfgMeta = getCompMeta(config.targetCompId);
          if (seemsToContainer) {
            // 如果被替换的走过一次尝试推荐容器了 下面只给他非容器
            return !compIsContainer(cfgMeta);
          }
          // 如果没被当成容器 则全都可以替换
          return true;
        }).filter(config => !resultAppearedIdSet.has(config.targetCompId));
        const replaceOptions = replaceConfigs.map(config => {
          const meta = getCompMeta(config.targetCompId);
          return {
            ...getBaseReplaceComponentOption(meta),
            applyCallback: this.genApplyCallback(config, ctx, layout, path)
          };
        })
        // 确保出现在替换列表中的分类顺序和组件面板的顺序一致
        .sort((a, b) => parseInt(a.type, 10) - parseInt(b.type, 10));
        ops.push(...replaceOptions);
      }
      {
        const replaceConfigs = this.lazyReplaceConfigs(ctx, compId);
        // 旧的fitData的组件替换逻辑所产生的结果 只会替换binding  这里Mixin一下其他部分的替换
        const resultFromDesignerHook = result.filter(item => item.from === 'designerDefaultHook');
        resultFromDesignerHook.forEach(item => {
          // item保持了引用 直接改obj,
          const compId = get(item, 'layout.type') || get(item, 'id');
          const config = replaceConfigs.find(config => compId === config.targetCompId);
          if (!config) return;
          const originCb = item.applyCallback;
          if (!originCb) return;
          item.applyCallback = async (...args) => {
            try {
              const newLayout = this.config2Layout(config, ctx, layout);
              const finalLayout = merge({}, item.layout, newLayout);
              originCb({
                ...args,
                layout: finalLayout
              });
            } catch (e) {
              // 兜底
              originCb(...args);
            }
          };
        });
      }
      return [...ops, ...result];
    }));
    // 组件列表发生变化的时候，要清空缓存。一般发生在组件开发中。
    uc.$nextTick(() => {
      uc.$watch(() => uc.components, () => {
        this.compWhiteList.clear();
        this.replaceConfigsCache.clear();
      });
    });
  }
}
//# sourceMappingURL=index.js.map
